import React, { useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Lightbox from './Lightbox';
import useLightbox from '../hooks/useLightbox';

const INITIATIVES = [
  {
    id: 'farm',
    image: '/farm-santo-antao.webp',
  },
  {
    id: 'ocean',
    image: 'https://images.unsplash.com/photo-1518837695005-2083093ee35b?w=600&q=80',
  },
  {
    id: 'artcraft',
    image: 'https://customer-assets.emergentagent.com/job_respiro-retreat/artifacts/5x8q16ms_e4d29087-0250-4e8b-b014-5a2df9b8ff67.jpg',
  },
];

const InitiativeCard = ({ initiative, compact, onImageClick }) => {
  const { t } = useLanguage();
  const title = t(`community.${initiative.id}.title`);

  return (
    <div
      className={compact
        ? 'flex-shrink-0 w-[280px] bg-white/80 backdrop-blur-sm border border-black/5 overflow-hidden snap-center'
        : 'bg-white/80 backdrop-blur-sm border border-black/5 overflow-hidden'}
      data-testid={compact ? `community-card-${initiative.id}` : `community-card-desktop-${initiative.id}`}
    >
      {/* Image */}
      <div 
        className="relative aspect-[4/3] cursor-pointer"
        onClick={onImageClick}
      >
        <img 
          src={initiative.image} 
          alt={title}
          loading="lazy"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
      </div>

      {/* Content */}
      <div className={compact ? 'p-4' : 'p-5'}>
        <h3 className={`font-serif text-black ${compact ? 'text-lg mb-2' : 'text-xl mb-3'}`}>
          {title}
        </h3>
        <p className={`text-black/70 font-light leading-relaxed text-justify ${compact ? 'text-xs' : 'text-sm'}`}>
          {t(`community.${initiative.id}.description`)}
        </p>
      </div>
    </div>
  );
};

const ALL_IMAGES = INITIATIVES.map(i => i.image);

const Community = () => {
  const { t } = useLanguage();
  const scrollContainerRef = useRef(null);
  const lightbox = useLightbox(ALL_IMAGES.length);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      // Scroll by card width + gap (300px + 24px)
      const scrollAmount = 324;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section 
      id="community" 
      className="py-10 md:py-12 bg-neutral-50" 
      data-testid="community-section"
    >
      <div className="max-w-[1400px] mx-auto">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto mb-12 px-6 md:px-12 text-center">
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-black mb-6">
            {t('community.title')}
          </h2>
          <p className="text-lg md:text-xl text-black/70 font-light">
            {t('community.subtitle')}
          </p>
        </div>

        {/* Mobile: Horizontal Scroll */}
        <div className="relative lg:hidden">
          <div
            ref={scrollContainerRef}
            className="flex gap-4 overflow-x-auto scrollbar-hide px-12 pb-4 snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {INITIATIVES.map((initiative, index) => (
              <InitiativeCard 
                key={initiative.id} 
                initiative={initiative} 
                compact 
                onImageClick={() => lightbox.open(index)} 
              />
            ))}
          </div>

          <button
            onClick={() => scroll('left')}
            className="absolute left-1 top-[100px] z-10 w-9 h-9 bg-white/90 hover:bg-white flex items-center justify-center shadow-lg rounded-full"
            data-testid="community-scroll-left"
          >
            <ChevronLeft size={18} className="text-black" />
          </button>

          <button
            onClick={() => scroll('right')}
            className="absolute right-1 top-[100px] z-10 w-9 h-9 bg-white/90 hover:bg-white flex items-center justify-center shadow-lg rounded-full"
            data-testid="community-scroll-right"
          >
            <ChevronRight size={18} className="text-black" />
          </button>
        </div>

        {/* Desktop Grid */}
        <div className="hidden lg:grid lg:grid-cols-3 gap-6 px-6 md:px-12">
          {INITIATIVES.map((initiative, index) => (
            <InitiativeCard 
              key={initiative.id} 
              initiative={initiative} 
              onImageClick={() => lightbox.open(index)} 
            />
          ))}
        </div>

        <Lightbox
          isOpen={lightbox.isOpen}
          onClose={lightbox.close}
          image={ALL_IMAGES[lightbox.currentIndex]}
          onPrev={lightbox.prev}
          onNext={lightbox.next}
          hasPrev={ALL_IMAGES.length > 1}
          hasNext={ALL_IMAGES.length > 1}
        />
      </div>
    </section>
  );
};

export default Community;
